import { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, AlertTriangle, TrendingUp, Building2, Users } from "lucide-react";
import { deptAvatarClass, deptBadgeClass } from "@/lib/employees";
import GapAnalysis from "@/components/GapAnalysis";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Stat({ label, value, hint }) {
  return (
    <div className="rounded-xl border border-border p-5 bg-white dark:bg-zinc-900">
      <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400 font-medium">{label}</p>
      <p className="mt-2 text-3xl font-bold tracking-tight text-zinc-900 dark:text-zinc-50 tabular-nums">{value}</p>
      {hint && <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{hint}</p>}
    </div>
  );
}

function CoverageBar({ pct }) {
  return (
    <div className="h-1.5 w-full bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
      <div
        className="h-full bg-emerald-500 transition-[width] duration-500"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export default function Insights({ onOpenProfile, onSkillClick, refreshKey }) {  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios.get(`${API}/insights/skills`)
      .then((r) => { if (mounted) setData(r.data); })
      .catch((e) => { if (mounted) setError(e.message); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [refreshKey]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center text-zinc-500 dark:text-zinc-400">
        Se calculează statisticile...
      </div>
    );
  }
  if (error) {
    return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-red-600">Eroare: {error}</div>;
  }
  if (!data) return null;

  const { totals, top_skills, rare_skills, department_coverage } = data;
  const empty = totals.unique_skills === 0;

  return (
    <div data-testid="insights-view" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400 font-medium inline-flex items-center gap-2">
          <Sparkles className="h-3 w-3" /> Analytics pentru management
        </p>
        <h1 className="mt-2 text-4xl sm:text-5xl font-bold tracking-tight text-zinc-900 dark:text-zinc-50">
          Skills Insights
        </h1>
        <p className="mt-3 text-zinc-600 dark:text-zinc-400 max-w-2xl">
          Privire de ansamblu asupra competențelor din eZipper. Identifică punctele forte, zonele cu risc (single-point-of-failure) și acoperirea per departament.
        </p>
      </div>

      {/* Totals */}
      <div data-testid="insights-totals" className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <Stat
          label="Angajați"
          value={totals.employees}
          hint={`${totals.with_skills} au declarat competențe`}
        />
        <Stat
          label="Competențe unice"
          value={totals.unique_skills}
          hint="în întreaga organizație"
        />
        <Stat
          label="Acoperire"
          value={`${totals.coverage_pct}%`}
          hint={`${totals.without_skills} angajați fără skills`}
        />
        <Stat
          label="Zone fragile"
          value={rare_skills.length}
          hint="competențe rare (≤2 persoane)"
        />
      </div>

      {empty ? (
        <div data-testid="insights-empty" className="text-center py-20 border border-dashed border-border rounded-xl">
          <Sparkles className="h-8 w-8 mx-auto text-zinc-300 dark:text-zinc-700" />
          <p className="mt-3 text-zinc-500 dark:text-zinc-400">
            Niciun angajat nu și-a adăugat încă competențe. <br />
            Deschide profilul oricărui coleg și adaugă primele competențe.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top skills */}
          <section className="rounded-xl border border-border bg-white dark:bg-zinc-900 overflow-hidden">
            <header className="px-6 py-4 border-b border-border flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-emerald-500" />
              <h2 className="text-sm font-semibold tracking-tight">Top competențe</h2>
              <span className="ml-auto text-[10px] uppercase tracking-[0.2em] text-zinc-400">cele mai prezente</span>
            </header>
            <ul data-testid="top-skills-list" className="divide-y divide-border">
              {top_skills.map((s) => (
                <li key={s.name} className="px-6 py-4">
                  <div className="flex items-center justify-between gap-3">
                    <button
                      data-testid={`top-skill-${s.name}`}
                      onClick={() => onSkillClick?.(s.name)}
                      className="text-sm font-medium text-zinc-900 dark:text-zinc-100 hover:underline underline-offset-4"
                    >
                      {s.name}
                    </button>
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
                      {s.count} {s.count === 1 ? "persoană" : "persoane"}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {s.holders.map((h) => (
                      <button
                        key={h.id}
                        onClick={() => onOpenProfile?.(h.id)}
                        className="inline-flex items-center gap-1.5 pl-1 pr-2 py-0.5 rounded-full text-xs hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                      >
                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold ${deptAvatarClass(h.department)}`}>
                          {h.initials}
                        </span>
                        <span className="text-zinc-700 dark:text-zinc-300">{h.name.split(" ").slice(0, 2).join(" ")}</span>
                      </button>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          </section>

          {/* Rare skills (SPOF) */}
          <section className="rounded-xl border border-border bg-white dark:bg-zinc-900 overflow-hidden">
            <header className="px-6 py-4 border-b border-border flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <h2 className="text-sm font-semibold tracking-tight">Competențe rare</h2>
              <span className="ml-auto text-[10px] uppercase tracking-[0.2em] text-zinc-400">≤ 2 persoane</span>
            </header>
            {rare_skills.length === 0 ? (
              <p className="px-6 py-8 text-sm text-zinc-500 dark:text-zinc-400">Nicio competență cu risc identificată — bună acoperire!</p>
            ) : (
              <ul data-testid="rare-skills-list" className="divide-y divide-border max-h-[480px] overflow-y-auto">
                {rare_skills.map((s) => (
                  <li key={s.name} className="px-6 py-4">
                    <div className="flex items-center justify-between gap-3">
                      <button
                        data-testid={`rare-skill-${s.name}`}
                        onClick={() => onSkillClick?.(s.name)}
                        className="text-sm font-medium text-zinc-900 dark:text-zinc-100 hover:underline underline-offset-4"
                      >
                        {s.name}
                      </button>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${s.count === 1 ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300" : "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"}`}>
                        {s.count === 1 ? "1 singură" : "2"}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {s.holders.map((h) => (
                        <button
                          key={h.id}
                          onClick={() => onOpenProfile?.(h.id)}
                          className="inline-flex items-center gap-1.5 pl-1 pr-2 py-0.5 rounded-full text-xs hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                        >
                          <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold ${deptAvatarClass(h.department)}`}>
                            {h.initials}
                          </span>
                          <span className="text-zinc-700 dark:text-zinc-300">{h.name.split(" ").slice(0, 2).join(" ")}</span>
                        </button>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Department coverage */}
          <section className="lg:col-span-2 rounded-xl border border-border bg-white dark:bg-zinc-900 overflow-hidden">
            <header className="px-6 py-4 border-b border-border flex items-center gap-2">
              <Building2 className="h-4 w-4 text-blue-500" />
              <h2 className="text-sm font-semibold tracking-tight">Acoperire pe departament</h2>
              <span className="ml-auto text-[10px] uppercase tracking-[0.2em] text-zinc-400">% angajați cu skills</span>
            </header>
            <ul data-testid="dept-coverage-list" className="divide-y divide-border">
              {department_coverage.map((d) => (
                <li key={d.name} className="px-6 py-4">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full ring-1 ring-inset ${deptBadgeClass(d.name)}`}>
                        {d.name}
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs text-zinc-500 dark:text-zinc-400">
                        <Users className="h-3 w-3" /> {d.total}
                      </span>
                    </div>
                    <span className="text-sm font-semibold tabular-nums text-zinc-900 dark:text-zinc-100">
                      {d.coverage_pct}%
                    </span>
                  </div>
                  <CoverageBar pct={d.coverage_pct} />
                  {d.top_skills.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 self-center">Top:</span>
                      {d.top_skills.map((s) => (
                        <button
                          key={s.name}
                          onClick={() => onSkillClick?.(s.name)}
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
                        >
                          {s.name}
                          <span className="opacity-50">{s.count}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </section>
        </div>
      )}

      {!empty && <GapAnalysis onOpenProfile={onOpenProfile} refreshKey={refreshKey} />}
    </div>
  );
}
